import {
  MouseEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIsMobile } from '../useIsMobile';
import { UseCustomCarouselReturn, UseCustomCarrouselProps } from './props';

/**
 * Custom hook to create a carousel with drag and auto-scroll functionality.
 *
 * @param {UseCustomCarrouselProps} options - The options for the carousel.
 * @returns {UseCustomCarouselReturn} - The carousel state and handlers.
 *
 * Example usage:
 * const {
 *   containerRef,
 *   isDragging,
 *   handleMouseDown,
 *   handleMouseMove,
 *   handleMouseLeave,
 *   handleMouseUp,
 *   handleDragLeft,
 *   handleDragRight,
 *   hasScroll,
 *   hasScrollLeft,
 *   hasScrollRight,
 *   handleContainerScroll,
 *   scrollLeft,
 *   currentStep,
 *   onStepClick,
 *   steps,
 * } = useCustomCarousel({ autoScroll: true, timeout: 3000 });
 */
export function useCustomCarousel(
  options?: UseCustomCarrouselProps,
): UseCustomCarouselReturn {
  const {
    onStepChange,
    size,
    autoScroll,
    timeout,
    currentIndex,
    scrollToCurrentOnEnter,
    slideBy = 1,
    direction = 'left',
  } = options ?? {};

  const isMobile: boolean = useIsMobile();

  const isFirstMount: MutableRefObject<boolean> = useRef<boolean>(true);
  const containerRef: MutableRefObject<HTMLDivElement> =
    useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [startX, setStartX] = useState<number>(0);
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const [hasScroll, setHasScroll] = useState<boolean>(false);
  const [hasScrollLeft, setHasScrollLeft] = useState<boolean>(false);
  const [hasScrollRight, setHasScrollRight] = useState<boolean>(false);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);

  const totalItems: number = containerRef.current
    ? containerRef.current.children.length
    : 0;
  const itemsPerView: number = isMobile ? 1 : (size ?? 1);

  const maxSteps: number = isMobile
    ? totalItems
    : Math.max(Math.ceil((totalItems - itemsPerView) / slideBy) + 1, 1);

  /**
   * Handle mouse down event to start dragging.
   *
   * @param {MouseEvent<HTMLDivElement>} e - The mouse event.
   *
   * Example usage:
   * handleMouseDown(event);
   */
  const handleMouseDown = useCallback((e: MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setStartX(e.pageX - e.currentTarget.offsetLeft);
    setScrollLeft(e.currentTarget.scrollLeft);
  }, []);

  /**
   * Handle mouse move event to drag the carousel.
   *
   * @param {MouseEvent<HTMLDivElement>} e - The mouse event.
   *
   * Example usage:
   * handleMouseMove(event);
   */
  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!isDragging) return;

      e.preventDefault();

      const x: number = e.pageX - e.currentTarget.offsetLeft;
      const walk: number = (x - startX) * 2;
      e.currentTarget.scrollLeft = scrollLeft - walk;
    },
    [isDragging, scrollLeft, startX],
  );

  /**
   * Handle mouse up event to stop dragging.
   *
   * Example usage:
   * handleMouseUp();
   */
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  /**
   * Handle mouse leave event to stop dragging.
   *
   * Example usage:
   * handleMouseLeave();
   */
  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  /**
   * Check if the container has scroll.
   *
   * Example usage:
   * checkIfHasScroll();
   */
  const checkIfHasScroll = useCallback(() => {
    const container: HTMLDivElement = containerRef.current;

    if (!container) {
      return;
    }

    setHasScroll(container.scrollWidth > container.clientWidth);
  }, []);

  /**
   * Check if the container can scroll to the left.
   *
   * Example usage:
   * checkIfScrollableLeft();
   */
  const checkIfScrollableLeft = useCallback(() => {
    const container: HTMLDivElement = containerRef.current;

    if (!container) {
      return;
    }

    setHasScrollLeft(container.scrollLeft > 0);
  }, []);

  /**
   * Check if the container can scroll to the right.
   *
   * Example usage:
   * checkIfScrollableRight();
   */
  const checkIfScrollableRight = useCallback(() => {
    const container: HTMLDivElement = containerRef.current;

    if (!container) {
      return;
    }

    setHasScrollRight(
      container.scrollLeft < container.scrollWidth - container.clientWidth,
    );
  }, []);

  /**
   * Handle drag event to scroll the container.
   *
   * @param {'left' | 'right'} direction - The direction to drag.
   *
   * Example usage:
   * handleDrag('left');
   */
  const handleDrag = useCallback(
    (direction: 'left' | 'right') => {
      const container: HTMLDivElement = containerRef.current;
      setIsDragging(false);

      if (!container) {
        return;
      }

      const childrenWidth =
        Number(container?.children?.[0]?.clientWidth ?? 220) + 16;
      let newScrollLeft: number;
      let newStep: number;
      const scrollWidth: number = childrenWidth * slideBy;

      switch (direction) {
        case 'left':
          newStep = Math.max(currentStep - 1, 0);
          newScrollLeft = container.scrollLeft - scrollWidth;
          break;
        case 'right':
          newStep = Math.min(currentStep + 1, maxSteps - 1);
          newScrollLeft = container.scrollLeft + scrollWidth;
          break;
      }

      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });

      setCurrentStep(newStep);
      setScrollLeft(newScrollLeft);
      onStepChange?.(newStep);
    },
    [containerRef, currentStep, maxSteps, onStepChange, slideBy],
  );

  /**
   * Handle drag left event.
   *
   * Example usage:
   * handleDragLeft();
   */
  const handleDragLeft = useCallback(() => {
    handleDrag('left');
  }, [handleDrag]);

  /**
   * Handle drag right event.
   *
   * Example usage:
   * handleDragRight();
   */
  const handleDragRight = useCallback(() => {
    handleDrag('right');
  }, [handleDrag]);

  /**
   * Scroll event listener to check scroll state.
   *
   * Example usage:
   * scrollEventListener();
   */
  function scrollEventListener() {
    const container: HTMLDivElement = containerRef.current;

    checkIfHasScroll();

    function handleResize() {
      checkIfHasScroll();
    }

    function handleScroll() {
      checkIfScrollableLeft();
      checkIfScrollableRight();
      setScrollLeft(Number(container?.scrollLeft));
    }

    if (container) {
      container.addEventListener('scroll', handleScroll);
      checkIfScrollableLeft();
      checkIfScrollableRight();
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);

      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }

  useEffect(scrollEventListener, [
    containerRef,
    isDragging,
    checkIfHasScroll,
    checkIfScrollableLeft,
    checkIfScrollableRight,
  ]);

  /**
   * Handle step listener to update current step.
   *
   * Example usage:
   * handleStepListener();
   */
  const handleStepListener = useCallback(() => {
    const container: HTMLDivElement = containerRef.current;

    if (!container) return;

    const childWidth: number = container.children[0]?.clientWidth + 16;

    const exactStep: number = scrollLeft / (childWidth * slideBy);
    const predictedStep: number = Math.round(exactStep);
    const boundedStep: number = Math.max(
      0,
      Math.min(predictedStep, maxSteps - 1),
    );

    if (currentStep === boundedStep) {
      return;
    }

    setCurrentStep(boundedStep);
    onStepChange?.(boundedStep);
  }, [currentStep, onStepChange, slideBy, maxSteps, scrollLeft]);

  useEffect(handleStepListener, [containerRef, handleStepListener]);

  /**
   * Handle step click event to scroll to the clicked step.
   *
   * @param {number} step - The step to scroll to.
   *
   * Example usage:
   * onStepClick(2);
   */
  const onStepClick = useCallback(
    (step: number) => {
      const container: HTMLDivElement = containerRef.current;

      if (!container) {
        return;
      }

      const childWidth: number =
        Number(container?.children?.[0]?.clientWidth) + 16;

      const scrollAmount: number = step * childWidth * slideBy;
      const maxScrollLeft: number =
        container.scrollWidth - container.clientWidth;
      const newScrollLeft: number = Math.min(scrollAmount, maxScrollLeft);

      setCurrentStep(step);
      setScrollLeft(newScrollLeft);
      onStepChange?.(step);

      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    },
    [onStepChange, slideBy],
  );

  /**
   * Handle auto-scroll interval to scroll the container automatically.
   *
   * Example usage:
   * handleAutoScrollInterval();
   */
  const handleAutoScrollInterval = useCallback(() => {
    const container: HTMLDivElement = containerRef.current;

    if (!container) {
      return;
    }

    const childWidth: number = container?.children?.[0]?.clientWidth + 16;
    const scrollAmount: number = childWidth * slideBy;

    if (direction === 'right') {
      // Scrolling Right-to-Left
      if (container.scrollLeft <= 0) {
        container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
        setCurrentStep(maxSteps - 1);
        return;
      }
      container.scrollTo({
        left: container.scrollLeft - scrollAmount,
        behavior: 'smooth',
      });
      setCurrentStep(prev => Math.max(prev - 1, 0));
    } else {
      if (
        container.scrollLeft + container.clientWidth >=
        container.scrollWidth
      ) {
        container.scrollTo({ left: 0, behavior: 'smooth' });
        setCurrentStep(0);
        return;
      }
      container.scrollTo({
        left: container.scrollLeft + scrollAmount,
        behavior: 'smooth',
      });
      setCurrentStep(prev => Math.min(prev + 1, maxSteps - 1));
    }
  }, [slideBy, maxSteps, direction]);

  /**
   * Handle auto-scroll to start the auto-scroll interval.
   *
   * Example usage:
   * handleAutoScroll();
   */
  const handleAutoScroll = useCallback(() => {
    if (!autoScroll) {
      return;
    }

    if (intervalId) {
      clearInterval(intervalId);
    }

    const id: NodeJS.Timeout = setInterval(
      handleAutoScrollInterval,
      timeout ?? 3500,
    );
    setIntervalId(id);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScroll, handleAutoScrollInterval, timeout]);

  useEffect(handleAutoScroll, [autoScroll, handleAutoScroll]);

  /**
   * Handle scroll to current index on enter.
   *
   * Example usage:
   * handleScrollToCurrentOnEnter();
   */
  function handleScrollToCurrentOnEnter() {
    if (
      !scrollToCurrentOnEnter ||
      !containerRef.current ||
      !currentIndex ||
      !isFirstMount.current
    ) {
      return;
    }

    isFirstMount.current = false;
    const childrenWidth: number =
      containerRef.current?.children?.[0]?.clientWidth + 16;
    containerRef.current.scrollTo({
      left: childrenWidth * currentIndex,
      behavior: 'smooth',
    });
  }

  /**
   * Adjust initial scroll if the direction is reversed.
   *
   * Example usage:
   * adjustInitialScrollIfReversed();
   */
  function adjustInitialScrollIfReversed() {
    if (direction === 'left' || !containerRef.current) {
      return;
    }

    containerRef.current.scrollTo({
      left: containerRef.current.scrollWidth,
      behavior: 'instant',
    });
    setCurrentStep(maxSteps - 1);
  }

  useEffect(adjustInitialScrollIfReversed, [direction, maxSteps]);

  useEffect(handleScrollToCurrentOnEnter, [
    currentIndex,
    scrollToCurrentOnEnter,
    handleDragRight,
  ]);

  return {
    containerRef,
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseLeave,
    handleMouseUp,
    handleDragLeft,
    handleDragRight,
    hasScroll,
    hasScrollLeft,
    hasScrollRight,
    handleContainerScroll: checkIfHasScroll,
    scrollLeft,
    currentStep,
    onStepClick,
    steps: maxSteps,
  };
}

export type { UseCustomCarouselReturn, UseCustomCarrouselProps };
